import { Fragment, useCallback, useMemo } from 'react';
import cross from '~/assets/icons/cross.svg';
import { playSound, Sound } from '~/assets/sounds';
import { PanelControls } from '~/components/ui/PanelControls';
import { ShopContainer } from '~/components/ui/Shop';
import { ShopBalance } from '~/components/ui/ShopBalance';
import { ShopHead } from '~/components/ui/ShopHead';
import { ShopPreview } from '~/components/ui/ShopPreview';
import { rage, useInputAction, useObservable } from '~/rage';
import { InputAction, InputKey, WalletType } from '~/shared';
import logo from '../../assets/images/logo.svg';
import { CategoryTab } from '../../components/CategoryTab';
import { ListTab } from '../../components/ListTab';
import { useBuy, useRepair, useShopPreview } from '../../hooks';
import {
	setActiveStage,
	StageTypes,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	StyledCloseButtonContainer,
	StyledControlsContainer,
	StyledRightContainer,
} from './VehicleService.styled';

const VehicleService = () => {
	const dispatch = useLayoutDispatch();
	const activeStage = useLayoutSelector(
		(state) => state.vehicleService.activeStage,
	);
	const activeCategory = useLayoutSelector(
		(state) => state.vehicleService.activeCategory,
	);
	const shopPreview = useShopPreview();
	const { handleBuy } = useBuy();
	const { handleRepair } = useRepair();
	const modalState = useObservable(rage.modal.getCurrentModal());

	const handleSelect = useCallback(() => {
		if (modalState) {
			return;
		}

		if (activeCategory === 'engine') {
			handleRepair();
			return;
		}

		dispatch(setActiveStage(StageTypes.List));
	}, [activeCategory, modalState]);

	const leftControls = useMemo(() => {
		if (activeStage === StageTypes.Category) {
			return [
				{
					text: 'Управление',
					keys: [
						InputKey.ArrowUp,
						InputKey.ArrowDown,
						InputKey.ArrowLeft,
						InputKey.ArrowRight,
					],
				},
				{
					text: 'Выбрать категорию',
					keys: [InputKey.Enter],
					onClick: handleSelect,
				},
			];
		}

		if (activeStage === StageTypes.List) {
			return [
				{
					text: 'Управление',
					keys: [InputKey.ArrowUp, InputKey.ArrowDown],
				},
				{
					text: 'Купить',
					keys: [InputKey.Enter],
					onClick: () => handleBuy(WalletType.Cash),
				},
			];
		}

		return [];
	}, [activeStage, handleBuy, handleSelect]);

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: () => {
				if (modalState) {
					return;
				}

				if (activeStage === StageTypes.List) {
					dispatch(setActiveStage(StageTypes.Category));
					return;
				}

				rage.vehicleServiceStation
					.exitMenu()
					.catch((err) => rage.notifications.showException(err));
			},
		},
	];

	useInputAction(InputAction.CommonMenuClose, () => {
		if (modalState) {
			return;
		}

		if (activeStage === StageTypes.List) {
			dispatch(setActiveStage(StageTypes.Category));
			return;
		}

		rage.vehicleServiceStation
			.exitMenu()
			.catch((err) => rage.notifications.showException(err));
	});

	return (
		<Fragment>
			<StyledRightContainer>
				<ShopBalance />
				<StyledCloseButtonContainer
					onClick={() => {
						rage.vehicleServiceStation.exitMenu();
						playSound(Sound.ClickButton);
					}}
					onMouseEnter={() => playSound(Sound.LinkCursor)}
				>
					<img src={cross} width={20} height={20} />
				</StyledCloseButtonContainer>
			</StyledRightContainer>
			<ShopContainer>
				<ShopHead
					img={logo}
					text={'Тех. обслуживание\nвашего транспорта'}
					height={30}
				/>
				{activeStage === StageTypes.List && <ListTab />}
				{activeStage === StageTypes.Category && <CategoryTab />}
			</ShopContainer>
			<StyledControlsContainer>
				<PanelControls controls={leftControls} align="left" />
				<PanelControls controls={rightControls} align="right" />
			</StyledControlsContainer>
			<ShopPreview {...shopPreview} />
		</Fragment>
	);
};

export { VehicleService };
